/** @module csi/table/_DndTableMixin */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dnd/Manager",
	"dojo/dom-class",
	"underscore",
	"csi/def",
	"csi/def/Action",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/table",
	"csi/view/_DndMixin",
	"csi/xml"
], function(declare, lang, aspect, dndManager, domClass, _, def, Action, csiHeading, html, locale, table, _DndMixin, xml) {

	var SELECTED = "csiSelected";
	var CSS_DRAG_ITEM_IN_PROGRESS = "csi-dnd-item-in-progress";
	var CSS_DND_AVATAR_CONTAINER = "csi-dnd-table-avatar-container";
	var CSS_DND_DISABLE_ITEM = "dojoDndItemDisable";
	var CSS_DND_GROUP = "tableViewGroup";

	return declare([_DndMixin], {
		/** @property {string} _previousRowId - previous row selected necessary to support SHIFT selection */
		_previousRowId: null,

		/** @property {HTMLElement} _unselectRowOnMouseUp - row to unselect on mouse up in certain cases */
		_unselectRowOnMouseUp: null,

		/**
		 * @override
		 * Recalculate drop position taking into account matrices and hierarchy; rows are only disabled
		 * for drag and drop based on grouping
		 */
		allowDropping: function(before, source, e) {
			const {anchor, current} = source;
			const hl = this.getHierarchyLevel(anchor);
			const row = this.getRefNode(e.target);
			const hlRow = this.getHierarchyLevel(row);
			const nextRow = this.getRefNodeNext(current || row, false, false, (row) => !this.isMergedRow(row) && this.isRowVisible(row));
			const hlNext = this.getHierarchyLevel(nextRow);
			let targetBefore = before;
			let targetNode = current;

			if (targetNode && targetNode.classList.contains(CSS_DND_GROUP)) {
				// always drop after group row, not before
				return {
					targetBefore: false,
					targetNode,
				};
			}

			// if drop node is a merged row, then change to the base row
			if (targetNode && this.isMergedRow(targetNode)) {
				targetNode = this.getRowMergeBase(targetNode);
			}

			if (targetNode && (hl === hlRow)) {
				// recalculate box and before in case row spans multiple rows
				const targetBox = targetNode.firstChild.getBoundingClientRect();
				targetBefore = (e.pageY - targetBox.y) < (targetBox.height / 2);

				if (!targetBefore) {
					// trying to drop after matrix row; now check if nextRow is a child or not
					if (!nextRow) {
						// at end of table
						if (targetNode === anchor) {
							// no reason to move last row
							targetNode = null;
						} else {
							// allow adding after last row in table
							targetNode = anchor.parentNode.lastChild;
							targetBefore = false;
						}
					} else if (hlNext > hl) {
						// next row is child, so insert before
						targetBefore = true;
					} else {
						// next row is valid, so insert before
						targetNode = nextRow;
						targetBefore = true;
					}
				}
			} else if (row && !nextRow) {
				// allow drop at end of table
				targetNode = this.getRowMerges(row).pop();
				targetBefore = false;
			} else if (row && nextRow && (hlNext <= hl)) {
				// allow drop before next row if over table row that is not at same level (such as row at child level), 
				// but the next row is less than or equal to the level of the dragged node; insert before next row
				// in case row has merged cells
				targetNode = nextRow;
				targetBefore = true;
			} else {
				// do not allow dropping elsewhere (such as on child level)
				targetNode = null;
			}

			return {
				targetBefore,
				targetNode
			};
		},

		/**
		 * Cleanup operations after canceling or dropping avatar
		 */
		cleanupDnd: function() {
			var manager = dndManager.manager();
			// reset the cursor position 
			manager.OFFSET_X = 0;
			manager.OFFSET_Y = 0;
			manager.nodes = _.uniq(manager.nodes);
			manager.nodes.forEach((node) => {
				if (this.domNode.contains(node)) {
					$(node).addClass(SELECTED);
				}
			});
			$("body").removeClass("csi-no-horizontal-scroll");
		},

		/**
		 * _DnDHandler[]: available DnD handlers for table views
		 */
		dndHandlers: [
			{
				allowMultiple: true,
				controller: "csi/action/DndOrderedController",
				css: "csiDndOrdered csi-table-dnd",
				dropInFront: false,

				canDrag: function(ctx) {
					return true;
				},

				isAppropriateFor: function(view) {
					return true;
				}
			}
		],

		/**
		 * Overrides avatar creation, create avatar with meaningful text from first cell or text with dragged row counts
		 * @param {DomNode} domNode
		 * @param (item) domNode
		 * @return {domNode}
		 */
		getFormattedAvatar: function(domNode, item) {
			var rows = this.getSelectedRows();
			rows = rows.filter(row => !this.isMergedRow(row));

			var rowCount = rows.length;
			var text;
			var params = this._getCellParams(rows[0].cells[0]);
			// AttrImage has no params, it also has no text
			if (params) {
				var h = params.h;
				var bTextAttribute = h.getAttrType() != xml.ATTR_BOOLEAN;
				// Boolean attribute does not have meaningful text (only false/true), we will rather use standard text "TableMoveRows"
				if (bTextAttribute && (rowCount == 1)) {
					// Use text from dragged row's first cell
					text = params.ren.headingToText(params.ctx, h);
				}
			}
			if (!text) {
				// Create text with dragged rows count 
				text = locale.resource("TableMoveRows", [rowCount]);
			}

			var avatarNode = html.create("div", {
				"class": "csi-dnd-table-avatar"
			});
			html.create("span", {
				"class": html.CSS_MATERIAL_ICON,
				innerText: "swap_vert"
			}, avatarNode);
			html.create("span", {
				"class": "csi-dnd-table-avatar-text",
				innerText: text
			}, avatarNode);

			var manager = dndManager.manager();
			// set the cursor position on dnd avatar
			manager.OFFSET_X = -16;
			manager.OFFSET_Y = -12;
			return avatarNode;
		},

		/**
		 * Returns the drag and drop root node
		 */
		getDndSourceNodes: function() {
			return [this.bodyNode];
		},

		/**
		 * initialize DnD action, Creates action for Dnd handle
		 */
		getDndAction: function() {
			var h = this._dndHandler;
			return lang.mixin(new Action(), {
				classController: h.controller,
				m_accessAction: h.canDrag,
				m_class: "dojoDndHandle",
				m_id: "Dnd",
				m_typeControl: def.ACTION_ICON,
				materialIcon: "more_vert",
				requirePrivateNode: false
			});
		},

		/**
		 * Return true if view explicitly restricts Dnd, or if Dnd is not enabled on the table
		 */
		isDndAvailable: function() {
			const {dndRestrict, groupSettings, _queryLevels} = this;
			const ctx = this.getContext();
			const view = ctx.view;

			if (dndRestrict || ctx.isReadOnly() || (groupSettings?.key1 && groupSettings?.key2)) {
				return false;
			}

			// allow on ordered tables configured with move action
			const movable = _.some(_queryLevels, (ql) => ql.bOrdered && !!ql.moveAction);
			if (movable) {
				return true;
			}

			// allow on tables displaying non-ordered items that persist their sort order in an attribute
			return view.getMainQueryLevel().typeSortAttribute && !view.get("customViewSortExists");
		},

		/**
		 * Determine if blocks in view are oriented vertically/ horizontal which allows CSS to better reflect drop zone
		 */
		isDndHorizontal: function() {
			return false;
		},

		/**
		 * @override
		 * Extra processing while dragging
		 */
		onDndDragExtension: function(source, e) {
			if (source.isDragging) {
				// clear row to unselect on mouse up if dragging first
				this._unselectRowOnMouseUp = null;

				// update drop position as mouse is dragged
				source._markTargetAnchor(source.before);
			}
		},

		/**
		 * @override
		 * Unselect row if CTRL+click on selected row - this is done on mouse up so that the row is not unselected before dragging
		 */
		onDndMouseUpExtension: function(source, e) {
			if (this._unselectRowOnMouseUp) {
				this._selectRow(this._unselectRowOnMouseUp, false);
				this._unselectRowOnMouseUp = null;
			}
		},

		/**   
		 * @override
		 * Processing selections of simple rows and matrix rows
		 */
		onDndSelectExtension: function(source, event) {
			let {shiftKey, target} = event;

			// do nothing if going to inline edit
			if (csiHeading.isHeadingNodeTabStop(target)) {
				return;
			}

			const selectedSelector = `.${SELECTED}:not(${html.getDataAttributeSelector(html.DATA_TABLE_MERGED)})`;
			const handleSelector = `.${html.CSS_DND_HANDLE}`;
			const sourceNode = source.node;
			const row = this.getRefNode(target);
			let ctrlKey = html.ctrlKeyPressed(event);
			let resultRow = this.getRowMergeBase(row) || row;

			// reset result row if cannot be selected
			if (resultRow && !this.isRefNode(resultRow, true)) {
				resultRow = null;
			}

			if (resultRow) {
				const hasDndHandle = (element) => !!element.querySelector(handleSelector);
				const selectedRow = sourceNode.querySelector(selectedSelector);
				const parentRow = this.getRowParent(resultRow);
				const parentSelectedRow = this.getRowParent(selectedRow);
				const resultRowIsSelected = resultRow.classList.contains(SELECTED);

				// do not allow multi-selection across branches of the tree (groups) or among rows that do and do not allow dragging
				if (selectedRow && ((parentRow != parentSelectedRow) || (hasDndHandle(resultRow) != hasDndHandle(selectedRow)))) {
					ctrlKey = shiftKey = false;
				}

				if (shiftKey) {
					const previousRow = this._previousRowId ? sourceNode.querySelector(`#${this._previousRowId}`) : null;

					// only select more rows when shift key is pressed - do not deselect rows
					if (!previousRow || (previousRow === resultRow)) {
						// select result row
						this._selectRow(resultRow, true);
					} else {
						// select any rows between previously selected row and result row that are not already selected
						const childrenRows = this.getRowChildren(parentRow, true);
						const resultIndex = childrenRows.indexOf(resultRow);
						const previousIndex = childrenRows.indexOf(previousRow);
						const n1 = Math.min(resultIndex, previousIndex);
						const n2 = Math.max(resultIndex, previousIndex);

						for (let i = n1; i <= n2; ++i) {
							this._selectRow(childrenRows[i], true);
						}
					}
				} else if (ctrlKey && resultRowIsSelected) {
					// wait until mouse up event to unselect the row so that if it is dragged, then it is still selected
					this._unselectRowOnMouseUp = resultRow;
				} else {
					// select result row and unselect all others (unless CTRL pressed or clicked on drag handle on already selected row)
					const clickedDragHandle = !!target.closest(handleSelector);
					const bReset = !ctrlKey && (!resultRowIsSelected || !clickedDragHandle)
					this._selectRow(resultRow, true, bReset);
				}
			} else if (!ctrlKey && !shiftKey) {
				// if no row clicked, then clear all selections unless CTRL or SHIFT pressed
				this.unselectAll();
			}

			// track previous selection to support SHIFT key processing
			this._previousRowId = resultRow ? resultRow.id : null;

			// sync source selection with row selections to indicate to Dojo what to drag
			source.selection = {};

			sourceNode.querySelectorAll(selectedSelector).forEach((row) => {
				source.selection[row.id] = 1;
			});
		},

		/**
		 * Return the children of the selected nodes
		 * @param {Object} node - selected node
		 * @return {Array} the children nodes
		 */
		_getDescendants: function(bExpandedOnly) {
			let rows = [];

			dndManager.manager().nodes.forEach((node) => {
				if (!bExpandedOnly || (this.getHierarchyState(node) === table.HS_MINUS)) {
					const children = this.getRowChildren(node, false, true);

					for (const child of children) {
						rows = rows.concat(this.getRowMerges(child));
					}
				}
			});

			return $(rows);
		},

		/**
		 * Unselect rows that are not being dragged
		 */
		_unselectNotDraggingRow: function() {
			$("." + SELECTED + ":not(." + CSS_DRAG_ITEM_IN_PROGRESS + ")").removeClass(SELECTED);
		},

		/**
		 * Hide the children of the node
		 */
		_hideDescendants: function() {
			this._getDescendants().hide();
		},

		/**
		 * Display the children of the node with expand state
		 */
		_showDescendants: function() {
			var descendants = this._getDescendants();
			if (descendants) {
				descendants.show();
			}
		},

		/**
		 * Return droppable group values for selected dnd items
		 * @param {DOMNode} domNodes - selected rows
		 * @return {Promise} 
		 */
		getDroppableGroupValues: function(domNodes) {
			// by default, only allow dropping within same group as selection(s)
			let value;

			for (const domNode of domNodes) {
				const qr = this.getQueryResultByDomNode(domNode);
				const qrGroup = qr.getParentGroup();

				if (qrGroup == null) {
					// no legal places to drop if no group value
					return Promise.resolve();
				}

				const {groupValue} = qrGroup;

				if (value === undefined) {
					// track first group value
					value = groupValue;
				} else if (value !== groupValue) {
					// selections are in multiple groups, so no places to drop
					return Promise.resolve();
				}
			}

			return Promise.resolve([value]);
		},

		/**
		 * Set the state of the table header based on flag parameter
		 * @param {boolean} flag - table header state
		 */
		_setTableHeaderState: function(flag) {
			domClass.toggle(this.headNode, "dnd-table-header-disable", flag);
		},

		/**
		 * Extension point to update a query result's group value 
		 * @param {Writer} writer
		 * @param {QueryResult} qr
		 * @param {Any} groupValue
		 */
		onDndUpdateGroupValue: function(writer, qr, groupValue) { },

		/**
		 * Add Avatar container class and item count; hide children of the dragging rows and unselect not dragging rows
		 * @param {DndSource} source
		 */
		onDndStartExtension: function(source) {
			var manager = dndManager.manager();

			this._unselectNotDraggingRow();
			this._setTableHeaderState(true);
			this._hideDescendants();

			// hide page horizontal scroll during dnd operation
			$("body").addClass("csi-no-horizontal-scroll");

			if (manager.avatar.node) {
				var jAvatarNode = $(manager.avatar.node);
				var rowCount = 0;

				jAvatarNode.addClass(CSS_DND_AVATAR_CONTAINER);

				var mergedNodes = [];
				_.each(manager.nodes, row => {
					mergedNodes = mergedNodes.concat(table.getMergedRows(row));
				});
				manager.nodes = mergedNodes;

				_.each(manager.nodes, function(val) {
					$(val).hasClass("dojoDndItem") && rowCount++;
				});
			}

			// determine groups where selected nodes can be dropped, and disable items within those groups
			if (this.groupSettings?.key1) {
				this._disableDnd();
				this.getDroppableGroupValues(manager.nodes).then((legalGroupValues) => this._disableDnd(legalGroupValues));
			}

			this.suspendHighlighting();
		},

		/**
		* Disable items that are not in the allowed groups
		* @param {string[]} [legalGroupValues] - group values which allow to drop selected items
		*/
		_disableDnd: function(legalGroupValues) {
			for (const domNode of this.bodyNode.children) {
				const qr = this.getQueryResultByDomNode(domNode);
				const qrGroup = qr.getParentGroup();
				const bDisable = !legalGroupValues || !qrGroup || !legalGroupValues.includes(qrGroup.groupValue);
				domNode.classList.toggle(CSS_DND_DISABLE_ITEM, bDisable);
			}
		},

		/**
		* Enable dnd feature for disable rows in table after cancel or drop operation
		*/
		_enableDnd: function() {
			$(this.bodyNode).find("." + CSS_DND_DISABLE_ITEM).removeClass(CSS_DND_DISABLE_ITEM);
			this.unsuspendHighlighting();
		},

		/**
		 * Remove drag styling and display the children of the row based on row state when canceled
		 */
		onDndCancelExtension: function() {
			this._showDescendants();
			this._setTableHeaderState(false);
			this._enableDnd();
		},

		/**
		 * Remove children nodes of the rows and reset the rows state when dropped
		 * @param {DndSource} source
		 * @param {DOMNode[]} nodes
		 */
		onDndDropExtension: function(source, nodes) {
			// ensure there is a place to drop the nodes
			const {current, targetAnchor} = source;

			if (!targetAnchor) {
				return false;
			} else if (targetAnchor !== current) {
				// make sure current is set to targetAnchor so that nodes are inserted in proper place
				source.current = targetAnchor;
			}

			// Make our code symmetrical to dojo/dnd/Source=>onDropInternal: this code deletes rows which onDropInternal supposed to move/restore
			// transferring nodes within the single source
			if (source.current && source.current.id in source.selection) {
				// do nothing
				return;
			}


			nodes.forEach(function(node) {
				this._deleteChildren(node);
				this.collapseRow(node);
			}, this);

			this._setTableHeaderState(false);
			this._enableDnd();

			// remove nodes from view - if you drop in same place as before, then nodes can be inserted in the wrong
			// order as the insertBefore node can be one of the nodes being dropped
			_.each(nodes, html.orphan);

			return true;
		},

		/**
		 * Display dnd handle for all selected rows while hovering on the selected row
		 * @param {Object} e - event object
		 */
		onMouseOverExtension: function(e) {
			$("." + SELECTED).toggleClass("csi-dnd-display-handle", this.isSelected(this.getRefNode(e.target)));
		},

		/**
		 * Add aspects to control Dnd behavior
		 */
		postCreate: function() {
			this.inherited(arguments);
			this.own(
				aspect.before(this, "_buildHead", lang.hitch(this, this._dndInitialize)),
				aspect.before(this, "onInsertBodyRow", lang.hitch(this, this._dndBuildResult)),
				aspect.before(this, "onRefreshFirstCell", lang.hitch(this, this._dndBuildResult))
			);
		},

		/**
		 * Sets dnd if its enabled on view
		 * @param {[Domnode]} tr
		 */
		_dndBuildResult: function(tr) {
			var qr = this._dndHandler && this.getQueryResultByDomNode(tr);

			if (!qr || (tr.parentNode != this.bodyNode)) {
				return;
			}

			var ql = qr.level;
			var bShowHandle;

			if (qr.bCompareNode) {
				bShowHandle = false;
			} else if (ql.moveAction) {
				// if ordered by vector, then show handle based on same rules as move action
				var ctx = qr.getContext();

				bShowHandle = ql.moveAction.hasAccess(ctx) && !ql.shouldDisableActions(ctx, qr.getNode());
			} else if (ql.typeSortAttribute) {
				// if ordered by attribute, then enable only if no sorting is applied
				var sortSettings = this.get("sortSettings");
				var bSorts = sortSettings && sortSettings.length;
				var bAccess = xml.nodeHasAccess(qr.getNode(), xml.RIGHT_WRITE, ql.typeSortAttribute);

				if (!ql.bMain || bSorts || !bAccess) {
					return;
				}

				bShowHandle = true;
			}

			if (!this.isMergedRow(tr)) {
				// show handle for rows that can be dragged and dropped
				if (bShowHandle) {
					var firstCell = tr.firstChild;
					var dndHandle = firstCell ? $(firstCell.firstChild).hasClass(html.CSS_DND_HANDLE) : null;
					if (!dndHandle) {
						var containerCell = html.create("span", {"class": html.CSS_DND_HANDLE}, firstCell, "first");
						this._dndAction.toHtml(this.getContext(), containerCell);
					}
				}
			}

			// apply dnd item class
			tr.classList.add("dojoDndItem");

			if (!ql.qm.bHierarchy) {
				// add class to row with no hierarchy or specific class for TableSwitchboard's rows
				if (this.defView.m_typeView === "TableSwitchboard") {
					tr.firstChild.classList.add("csi-dnd-tableSwitchBoard");
				}
				tr.firstChild.classList.add("csi-dnd-flatRowColumn");
			}

			this.inherited(arguments);
		},

		/**
		* filter only main rows from row with matrix
		* @param {DndSourceObject} target - dnd target
		* @param {DndSourceObject} source - dnd source
		* @param {DOMObject []} nodes - drop nodes
		* @param {boolean} - allow copy or not
		*/
		_dndDrop: function(target, source, nodes, copy) {
			nodes = _.filter(_.unique(nodes), function(node) {
				return !this.isMergedRow(node);
			}, this);
			this.inherited(arguments, [target, source, nodes, copy]);
		}
	});
});